import { useEffect, useRef, useState } from 'react';

// Railway terminates WebSocket connections after ~15 minutes (max request
// duration). A close is therefore EXPECTED, not an error — we always reconnect
// with exponential backoff (1s, 2s, 4s … capped at 30s).
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;
const PING_INTERVAL_MS = 25000;

/**
 * Single auto-reconnecting WebSocket connection.
 *
 * @param {string|null} url      ws(s):// URL incl. ?token=…  (null = don't connect)
 * @param {object}      options  { enabled }
 * @returns {{ status: 'idle'|'connecting'|'open'|'closed', lastMessage: object|null }}
 */
export default function useWebSocket(url, { enabled = true } = {}) {
  const [status, setStatus] = useState('idle');
  const [lastMessage, setLastMessage] = useState(null);

  const wsRef = useRef(null);
  const attemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const closedByUserRef = useRef(false);

  useEffect(() => {
    if (!enabled || !url) {
      setStatus('idle');
      return undefined;
    }

    closedByUserRef.current = false;

    const clearTimers = () => {
      clearTimeout(reconnectTimerRef.current);
      clearInterval(pingTimerRef.current);
    };

    const scheduleReconnect = () => {
      if (closedByUserRef.current) return;
      attemptsRef.current += 1;
      const delay = Math.min(
        BASE_BACKOFF_MS * 2 ** (attemptsRef.current - 1),
        MAX_BACKOFF_MS,
      );
      reconnectTimerRef.current = setTimeout(open, delay);
    };

    function open() {
      setStatus('connecting');

      let ws;
      try {
        ws = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0;
        setStatus('open');
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try { ws.send(JSON.stringify({ type: 'ping' })); } catch { /* socket gone */ }
        }, PING_INTERVAL_MS);
      };

      ws.onmessage = (event) => {
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        if (data?.type === 'ping') {
          try { ws.send(JSON.stringify({ type: 'pong' })); } catch { /* ignore */ }
          return;
        }
        if (data?.type === 'pong' || data?.type === 'connected') return;
        setLastMessage(data);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };

      ws.onclose = () => {
        clearInterval(pingTimerRef.current);
        setStatus('closed');
        scheduleReconnect();
      };
    }

    open();

    return () => {
      // Intentional teardown — suppress reconnect, close cleanly.
      closedByUserRef.current = true;
      clearTimers();
      const ws = wsRef.current;
      if (ws) {
        ws.onclose = null;
        try { ws.close(); } catch { /* ignore */ }
      }
    };
  }, [url, enabled]);

  return { status, lastMessage };
}
